import { memo, useContext, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/features';
import log from '@/lib/logger';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { ButtonGroup } from '@/shared/components/ui/button-group';
import { convertDateToTimezone, formatDateToFriendlyString } from '@/util/date-formatters';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ChargePointEnelSVG from 'assets/images/charge_point_enel.svg?react';
import ChargePointSVG from 'assets/images/charge_point.svg?react';
import TransactionInfo from 'components/ChargePointDetails/TransactionInfo';
import CircularProgress from 'components/CircularProgress';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorFallback from 'components/ErrorFallback';
import IconFilled from 'components/IconFilled';
import SectionTitle from 'components/SectionTitle';
import SelectElement from 'components/SelectElement';
import StatusLed from 'components/StatusLed';
import RestrictedComponent from 'containers/RestrictedComponent';
import { compareAsc } from 'date-fns';
import {
  fetchTransactionsByChargePointId,
  fetchTransactionsByChargePointIdPaginated,
  getSelectedTransactionSelector,
  getTransactionIsFetching,
  getTransactionsByChargePointIdSelector,
  loadTransactions,
  setSelectedTransaction,
} from 'features/Transactions';
import { SnackbarContext } from 'providers/SnackbarProvider';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getChargePointHeartbeat, resetChargePoint } from 'services/chargePoints';
import IntlMessages from 'util/IntlMessages';

const AdvancedOptionsDialog = ({ chargePoint, resetChargePoint, open, onClose }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [action, setAction] = useState(undefined);
  const [isUpdating, setIsUpdating] = useState(false);
  const { pushMessage } = useContext(SnackbarContext);

  const handleClose = () => {
    setMenuOpen(false);
  };

  const handleToggle = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleClick = (type) => {
    setMenuOpen(false);
    setAction({
      text: `${type} reset`,
      action: () => {
        setIsUpdating(true);
        resetChargePoint(chargePoint.id, type)
          .then(() => {
            setIsUpdating(false);
            pushMessage(`El ${type.toLowerCase()} reset se ha realizado con exito.`);
            onClose();
          })
          .catch((e) => {
            if (e.response.data.detail === 'response is None') {
              pushMessage('No hubo respuesta del servidor.');
            } else {
              pushMessage(`No se ha podido realizar el ${type.toLowerCase()} reset.`);
            }
            onClose();
            log.error(e);
          });
      },
    });
  };

  return (
    <Dialog
      className="advanced-options-dialog"
      open={open}
      aria-labelledby="dialog-title"
      onClose={onClose}
    >
      <DialogTitle id="dialog-title">
        <IntlMessages id="components.connector-details.advanced-options" />
      </DialogTitle>
      <div className="px-4 pb-4">
        <SectionTitle
          title={
            <span className="font-size-18">
              <IntlMessages id="components.charge-point-details.reset" />
            </span>
          }
        />
        {action ? (
          <div className="d-flex flex-column">
            {isUpdating ? (
              <CircularProgress />
            ) : (
              <>
                <p>
                  <IntlMessages id="components.charge-point-details.confirm-text-before" />
                  <span>{` ${action.text}. `}</span>
                  <IntlMessages id="components.charge-point-details.confirm-text-after" />
                </p>
                <div className="$tw-flex $tw-justify-end $tw-gap-2">
                  <Button onClick={action.action}>Continuar</Button>
                  <Button variant="secondary" onClick={() => setAction(undefined)}>
                    Cancelar
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <ButtonGroup aria-label="split button">
              <Button size="sm" onClick={() => handleClick('Soft')}>
                Ejecutar Soft Reset
              </Button>
              <Button
                size="sm"
                aria-controls={menuOpen ? 'split-button-menu' : undefined}
                aria-expanded={menuOpen ? 'true' : undefined}
                aria-label="popper"
                aria-haspopup="menu"
                onClick={handleToggle}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Menu
              className="simple-menu"
              id="simple-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleClose}
              PaperProps={{
                style: {
                  minWidth: 120,
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              }}
            >
              <MenuItem onClick={() => handleClick('Hard')}>
                <IntlMessages id="components.charge-point-details.hard-reset" />
              </MenuItem>
            </Menu>
          </>
        )}
      </div>
    </Dialog>
  );
};

const ChargePointInfo = ({ chargePointConnectors }) => {
  const [heartbeat, setHeartbeat] = useState(null);
  const { timezone } = useContext(UserSessionContext);
  const chargePoint = useAppSelector((state) => state.chargePoints.selectedChargePoint);
  const chargePointImg =
    chargePoint.latest_boot_notification &&
    chargePoint.latest_boot_notification.charge_point_vendor === 'StarCharge' ? (
      <ChargePointEnelSVG />
    ) : (
      <ChargePointSVG />
    );

  const filteredConnectors = chargePointConnectors?.filter(
    (connector) => connector.verbose_name !== 'Input'
  );

  useEffect(() => {
    const heartBeatId = chargePoint.latest_heartbeat?.id || chargePoint.latest_heartbeat;
    if (heartBeatId) {
      getChargePointHeartbeat(heartBeatId)
        .then((response) => setHeartbeat(response.data?.current_time))
        .catch((error) => {
          log.error('Error obteniendo heartBeat:', error);
        });
    }
  }, [chargePoint.id, chargePoint.heartbeat, chargePoint.latest_heartbeat]);

  return (
    <>
      <IconFilled icon={chargePointImg} percentage={0} />
      <div className="d-flex flex-column ml-3">
        <h3 className="mb-3 text-break">
          <b>{chargePoint.verbose_name || chargePoint.name}</b>
        </h3>
        <h6 className="subtitle mb-3 font-weight-normal">
          {`${filteredConnectors.length} `}
          <IntlMessages id="common.labels.connectors" />
        </h6>
        <div className="d-flex align-items-center">
          <StatusLed status={chargePoint.status} size="12px" />
          <h6 className="ml-2 mb-0">
            {chargePoint.status === 'Waiting' ? 'Reconnecting' : chargePoint.status}
          </h6>
        </div>
        {chargePoint.status === 'Offline' && heartbeat && (
          <div className="subtitle mt-1 font-weight-normal">
            <p>desde {formatDateToFriendlyString(heartbeat, timezone)}</p>
          </div>
        )}
      </div>
    </>
  );
};

const ChargePointDetails = ({ chargePoint, chargePointConnectors }) => {
  const dispatch = useDispatch();
  const transactionIsFetching = useSelector(getTransactionIsFetching, shallowEqual);
  const transactions = useSelector(
    (state) => getTransactionsByChargePointIdSelector(state, chargePoint.id),
    shallowEqual
  );
  const selectedTransaction = useSelector(getSelectedTransactionSelector, shallowEqual);
  const [dialogOpen, setDialogOpen] = useState(false);
  const page = useRef(2);
  const { timezone } = useContext(UserSessionContext);

  useEffect(() => {
    if (chargePoint) dispatch(fetchTransactionsByChargePointId(chargePoint.id, null, null, 10, 1));
    return () => {
      dispatch(setSelectedTransaction(null));
      dispatch(loadTransactions({}));
    };
  }, [dispatch, chargePoint]);

  const onTransactionClick = (transactionId) => {
    dispatch(setSelectedTransaction(null));
    if (selectedTransaction && selectedTransaction.id === transactionId)
      dispatch(setSelectedTransaction(null));
    dispatch(
      setSelectedTransaction(
        Object.values(transactions).find((transaction) => transaction.id === transactionId)
      )
    );
  };

  const handleLazyLoading = () => {
    const select = document.querySelector(
      '.MuiPaper-root.MuiMenu-paper.MuiPopover-paper.MuiPaper-elevation8.MuiPaper-rounded'
    );
    if (page.current && select.scrollTop + select.clientHeight === select.scrollHeight) {
      dispatch(fetchTransactionsByChargePointIdPaginated(chargePoint.id, 10, page.current)).then(
        (response) => {
          if (response.next) page.current += 1;
          else page.current = null;
        }
      );
    }
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="charge-point-details-container d-flex flex-column flex-fill">
        <SectionTitle
          title={
            <div className="text-break">
              {!chargePoint.verbose_name.toLowerCase().includes('cargador') ? (
                <IntlMessages id="common.labels.charge-point" />
              ) : null}
              <span>{` ${chargePoint.verbose_name || chargePoint.name}`}</span>
              <span className="charge-point-details-vendor">{`  -  ${chargePoint.vendor}`}</span>
            </div>
          }
        />
        <div className="vehicle-data d-flex align-items-center justify-content-center">
          <ChargePointInfo
            chargePoint={chargePoint}
            chargePointConnectors={chargePointConnectors}
          />
        </div>
        <Divider className="my-3" />
        <div>
          <h5>
            <IntlMessages id="components.charge-point-details.transaction-history" />
          </h5>
          <div>
            {transactionIsFetching ? (
              <div style={{ maxHeight: 49 }}>
                <CircularProgress />
              </div>
            ) : (
              <div className="d-flex flex-column">
                {transactions &&
                Object.keys(transactions).length &&
                selectedTransaction &&
                Object.keys(selectedTransaction).length ? (
                  <>
                    <SelectElement
                      className="mb-3 align-items-center justify-content-between"
                      label={<IntlMessages id="components.charge-point-details.date" />}
                      options={Object.values(transactions).sort((a, b) =>
                        compareAsc(
                          convertDateToTimezone(b.timestamp_start, timezone),
                          convertDateToTimezone(a.timestamp_start, timezone)
                        )
                      )}
                      selectedId={selectedTransaction ? selectedTransaction.id : ''}
                      optionLabel="timestamp_start"
                      idAttribute="id"
                      onChange={onTransactionClick}
                      displayLabelAs={(label) => formatDateToFriendlyString(label, timezone)}
                      onScroll={handleLazyLoading}
                    />
                    <TransactionInfo
                      transaction={selectedTransaction}
                      transactionConnector={
                        chargePointConnectors
                          ? chargePointConnectors.find(
                              (connector) => connector.id === selectedTransaction.connector
                            )
                          : null
                      }
                    />
                  </>
                ) : (
                  <span>
                    <IntlMessages id="components.charge-point-details.no-transactions" />
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <RestrictedComponent>
          <div className="d-flex flex-column flex-fill justify-content-end">
            <Button variant="secondary" onClick={() => setDialogOpen(true)}>
              Opciones avanzadas
            </Button>
          </div>
        </RestrictedComponent>
        <AdvancedOptionsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          chargePoint={chargePoint}
          resetChargePoint={resetChargePoint}
        />
      </div>
    </ErrorBoundary>
  );
};

export default memo(ChargePointDetails, (prevProps, nextProps) => {
  const prevChargePointId = prevProps?.chargePoint?.id;
  const nextChargePointId = nextProps?.chargePoint?.id;
  if (prevChargePointId && nextChargePointId && prevChargePointId === nextChargePointId)
    return true;
  return false;
});
